import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';

const ApplicantLogin = () => {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicant-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username.trim().toLowerCase(), password: form.password }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.detail || 'Login failed');
      }
      const data = await res.json();
      localStorage.setItem('nexus_applicant_token', data.token);
      localStorage.setItem('nexus_applicant_id', data.applicant?.id || '');
      nav('/applicant-portal');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <img src="/nexus-patch.png" alt="Nexus" className="w-28 h-28 mx-auto mb-3 object-contain" data-testid="applicant-login-logo" />
          <h1 className="text-2xl font-heading font-bold text-white">Applicant Portal</h1>
          <p className="text-xs text-slate-400 font-mono uppercase tracking-[0.25em] mt-1">Nexus Investigation Agency</p>
        </div>
        <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-sm p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm" data-testid="applicant-login-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider font-mono">Email / Username</Label>
            <Input
              data-testid="applicant-login-username"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              className="bg-white/5 border-white/10 text-white mt-1"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider font-mono">Password</Label>
            <Input
              data-testid="applicant-login-password"
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="bg-white/5 border-white/10 text-white mt-1"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-[#0B192C] hover:bg-slate-200 font-heading font-semibold rounded-sm"
            data-testid="applicant-login-submit"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </Button>
          <div className="flex justify-between text-xs font-mono uppercase tracking-wider pt-2 border-t border-white/10">
            <Link to="/login" className="text-slate-400 hover:text-slate-200">Staff Login</Link>
            <Link to="/apply" className="text-amber-300 hover:text-amber-200">Apply to Nexus →</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicantLogin;
